// Non-loop text transformation pipeline for expanded sources.
// Example: placeholder replacement -> array refs -> arithmetic.
import type { LuisArrayVariables } from '../../types'
import type { LuisCompileWarning, LuisObjectVariables } from '../../types'
import type { LoopScope } from './types'
import { replaceLoopPlaceholders } from './placeholders'
import { replaceArrayVariableReferencesInline } from './array-references'
import { evaluateMultiplicationExpressions } from './arithmetic'
import { evaluatePlainAddSubExpressions } from './plain-addition'
import { normalizeSelectorColonBeforeBrace } from './selector-normalize'
// Check add/sub transform flow.
// Check multiplication transform flow.

// Transform non-loop text by applying placeholders and numeric multiplications.
export function transformPlainText(
  source: string,
  variables: Record<string, string>,
  arrays: LuisArrayVariables,
  objectVariables: LuisObjectVariables,
  warnings: LuisCompileWarning[],
  scope: LoopScope,
): string {
  let output = source
  // Resolve placeholders only when source contains `$` marker.
  if (source.includes('$')) {
    output = replaceLoopPlaceholders(output, scope, arrays, variables, objectVariables)
  } else {
    // Keep text unchanged when placeholder markers are absent.
  }

  // Resolve array references only when var-like tokens and arrays exist.
  if (output.includes('var(')) {
    output = replaceArrayVariableReferencesInline(output, arrays, objectVariables, variables, warnings)
  } else {
    // Keep text unchanged when inline var references are absent.
  }

  // Evaluate plain-text additions only when source contains `+` or `-`.
  if (output.includes('+') || output.includes('-')) {
    output = evaluatePlainAddSubExpressions(output, variables)
  } else {
    // Keep text unchanged when add/sub expressions are absent.
  }

  // Evaluate multiplications only when source contains `*`.
  if (output.includes('*')) {
    output = evaluateMultiplicationExpressions(output, variables)
  } else {
    // Keep text unchanged when multiplication expressions are absent.
  }

  // Return fully transformed non-loop segment.
  return normalizeSelectorColonBeforeBrace(output)
}
